import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AssociationService } from '../../../../../../../services/association/association.service';
import { CallbackResult, CallbackResultStatus, dateStringFromISO8601 } from '../../../../../../../utils';
import { Association, AssociationFilters, AssociationMember } from '../../../../../../../models/association';
import { Customer } from '../../../../../../../models/customer';
import { ListResponse } from '../../../../../../../models/list-response';
import { AccountService } from '../../../../../../../services/account/account.service';
import { OrganizationConfigService } from '../../../../../../../services/organization-config/organization-config.service';


@Component({
  selector: 'app-add-customer-to-association-modal',
  templateUrl: './add-customer-to-association-modal.component.html',
  styleUrls: ['./add-customer-to-association-modal.component.css']
})
export class AddCustomerToAssociationModalComponent implements OnInit {

  @Input() customer: Customer;
  @Input() savedCallback: (result: CallbackResult) => undefined;

  public associationMember: AssociationMember = {
    id: null,
    url: null,
    association_id: null,
    customer_id: null,
    association_url: null,
    customer_url: null,
    association_name: null,
    association_verification_requirements: null,
    association_shows_reverification_alert: false,
    customer_first_name: null,
    customer_last_name: null,
    customer_birthday: null,
    last_reverified_at: null,
    is_pending_reverification: false,
    has_pending_application: false,
    application_is_hidden_from_inbox: false,
    application_document: null,
    application_text: '',
    approved_by_id: null,
    approved_by_first_name: null,
    approved_by_last_name: null,
    approved_at: null,
    created_at: null,
    updated_at: null
  };

  public loadingAssociations = false;
  public associationFilters: AssociationFilters = {
    search: ''
  };
  public associationFiltersChangedSubject = new Subject<null>();
  public associationList: ListResponse<Association>;
  // The number of list requests. Used to ensure that only the most recent
  // request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private associationListRequestCount = 0;

  public selectedAssociation: Association = null;

  public loadingSubmit = false;
  public errors: any = {};
  public errorMessage = '';
  public serverError: number = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public dateStringFromISO8601 = dateStringFromISO8601;

  constructor(
    public accountService: AccountService,
    public activeModal: NgbActiveModal,
    public orgConfigService: OrganizationConfigService,
    private associationService: AssociationService
  ) {}

  ngOnInit() {
    if (!this.accountService.getMyAccount().can_manage_members) {
      return;
    }

    this.associationMember.customer_id = this.customer.id;

    // Handle changes to the filters by reloading the results,
    // subject to debounce.
    this.associationFiltersChangedSubject.pipe(
      map(() => { this.loadingAssociations = true; }),
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadAssociations();
    });
  }

  /**
   * Handle a change in the filters. The results will be filtered according to
   * the new values.
   */
  public handleAssociationFilterChange() {
    // Reset the selected association.
    this.selectedAssociation = null;

    // Reset errors.
    this.errorMessage = '';
    this.errors = {};

    this.associationFiltersChangedSubject.next();
  }

  /**
   * Load the page of results corresponding to the current page and filter
   * selections.
   */
  public loadAssociations() {
    this.loadingAssociations = true;

    // Increment the request count and save a local copy.
    this.associationListRequestCount++;
    const thisRequestCount = this.associationListRequestCount;

    if (this.associationFilters.search === '') {
      this.associationList = null;
      this.loadingAssociations = false;
      return;
    }

    this.associationService
      .getAssociations(
        1,
        // 60 allows us to still display 50 results if we need to remove up to
        // 10 items from the list.
        60,
        this.associationFilters
      )
      .then(response => {
        // Only update the list if there has not been a more recent request initiated.
        if (thisRequestCount === this.associationListRequestCount) {
          this.associationList = response;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.associationListRequestCount) {
          this.loadingAssociations = false;
        }
      });
  }

  /**
   * Whether a non-zero number of results have been loaded.
   */
  public resultsPresent(): boolean {
    return (
      this.associationList &&
      this.associationList.results &&
      (this.associationList.results.length > 0)
    );
  }

  /**
   * Handle a click on one of the results by displaying the association's
   * name in the search input.
   */
  public selectAssociation(association: Association) {
    this.selectedAssociation = association;
    this.associationFilters.search = association.name;
  }

  /**
   * Whether a valid set of inputs have been selected.
   * Used to determine whether the save button is clickable.
   */
  public validInputSelected() {
    return this.selectedAssociation !== null;
  }

  public handleSave() {
    this.loadingSubmit = true;

    this.errorMessage = '';
    this.errors = {};

    this.associationMember.association_id = this.selectedAssociation.id;

    this.associationService
      .addAssociationMember(this.associationMember)
      .then(newMember => {
        this.savedCallback({
          text: 'Customer successfully added to association.',
          status: CallbackResultStatus.success
        });
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.errors = reason.error;
          this.errorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to add the customer to this association.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingSubmit = false; });
  }

}
